import React, { useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { ChartDefinitionDrawer } from '@/components/visuals/ChartDefinitionDrawer';
import { useAnalyticsPins } from '@/hooks/useAnalyticsPins';
import { useAnalyticsChartData } from '@/pages/analytics/overview';
import { CHART_REGISTRY, ALL_CATEGORIES } from '@/pages/analytics/overview/registry';
import type { ChartDefinition } from '@/pages/analytics/overview/registry';
import type { ChartCategory } from '@/pages/analytics/overview/registry';
import {
  CATALOG_LIBRARY_SECTIONS,
  CATALOG_LIBRARY_FILTER_GROUPS,
  isCatalogSnapshotTrendChart,
  normalizeCatalogLibParam,
  chartIdsInCatalogLibraryGroup,
} from '@/pages/analytics/catalogLibrarySections';
import { cn } from '@/lib/utils';
import { BarChart3, Pin, PinOff, Info, Download, ChevronDown } from 'lucide-react';
import LoadingState from '@/components/ui/LoadingState';
import Tooltip from '@/components/ui/tooltip';

const defaultPinnedIds = CHART_REGISTRY.filter((c) => c.pinned).map((c) => c.id);
const categories: Array<ChartCategory | 'All'> = ['All', ...ALL_CATEGORIES];

function chartRequiresSnapshots(chart: ChartDefinition): boolean {
  return chart.requiresSnapshotHistory === true;
}

function focusHref(chartId: string): string {
  return `/app/analytics/charts?view=focus&chart=${chartId}`;
}

const ACTION_ICON_BTN =
  'inline-flex h-8 min-h-8 w-8 min-w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background';

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const libGroup = normalizeCatalogLibParam(searchParams.get('lib'));
  const categoryParam = (searchParams.get('category') as ChartCategory | 'All') || 'All';
  const searchQuery = searchParams.get('q') || '';
  const pinnedOnly = searchParams.get('pinned') === 'true';

  const { pinnedIds, handleTogglePin } = useAnalyticsPins({ defaultPinnedIds, initialShowPinnedOnly: true });

  const [definitionChart, setDefinitionChart] = React.useState<(typeof CHART_REGISTRY)[0] | null>(null);

  const setUrlParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleLibChange = useCallback(
    (lib: string) => {
      setUrlParams({ lib: lib === 'all' ? null : lib });
    },
    [setUrlParams]
  );

  const handleCategoryChange = useCallback(
    (cat: ChartCategory | 'All') => {
      setUrlParams({ category: cat === 'All' ? null : cat });
    },
    [setUrlParams]
  );

  const handleSearchChange = useCallback(
    (q: string) => {
      setUrlParams({ q: q || null });
    },
    [setUrlParams]
  );

  const handlePinnedToggle = useCallback(() => {
    setUrlParams({ pinned: pinnedOnly ? null : 'true' });
  }, [pinnedOnly, setUrlParams]);

  const visibleCharts = useMemo(() => {
    let list = CHART_REGISTRY;
    const libIds = chartIdsInCatalogLibraryGroup(libGroup);
    if (libIds) {
      list = list.filter((c) => libIds.has(c.id));
    }
    if (pinnedOnly) {
      const pinnedSet = new Set(pinnedIds);
      list = list.filter((c) => pinnedSet.has(c.id));
    }
    if (categoryParam !== 'All') {
      list = list.filter((c) => c.category === categoryParam);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) => c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
      );
    }
    return list;
  }, [libGroup, categoryParam, searchQuery, pinnedOnly, pinnedIds]);

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  const {
    loading: chartsLoading,
    hasData: hasChartData,
    trendsData,
    renderChart,
  } = useAnalyticsChartData({ showDataLabels: false });

  const historyReady = !trendsData.loading;
  const historyOk = trendsData.hasHistory && (trendsData.points?.length ?? 0) >= 2;

  const { liveCharts, blockedSnapshotCharts } = useMemo(() => {
    const live: ChartDefinition[] = [];
    const blocked: ChartDefinition[] = [];
    for (const c of visibleCharts) {
      if (!chartRequiresSnapshots(c)) {
        live.push(c);
      } else if (!historyReady) {
        live.push(c);
      } else if (historyOk) {
        live.push(c);
      } else {
        blocked.push(c);
      }
    }
    return { liveCharts: live, blockedSnapshotCharts: blocked };
  }, [visibleCharts, historyOk, historyReady]);

  const liveIds = useMemo(() => new Set(liveCharts.map((c) => c.id)), [liveCharts]);
  const orderedLiveCharts = useMemo(() => CHART_REGISTRY.filter((c) => liveIds.has(c.id)), [liveIds]);

  const trendLiveCharts = useMemo(
    () => orderedLiveCharts.filter((c) => isCatalogSnapshotTrendChart(c.id)),
    [orderedLiveCharts]
  );
  const coreLiveCharts = useMemo(
    () => orderedLiveCharts.filter((c) => !isCatalogSnapshotTrendChart(c.id)),
    [orderedLiveCharts]
  );

  const previewHeight = 260;

  const chipBtn = 'rounded-md border px-3 py-1.5 text-xs transition-colors';
  const chipActiveClass = 'border-border bg-background font-semibold text-foreground shadow-sm';
  const chipInactiveClass = 'border-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground';

  const renderCard = useCallback(
    (chart: ChartDefinition) => {
      const isPinned = pinnedSet.has(chart.id);
      const toFocus = focusHref(chart.id);

      return (
        <article
          key={chart.id}
          className={cn(
            'relative flex flex-col overflow-hidden rounded-lg border border-border bg-card',
            'shadow-sm transition-[box-shadow,border-color,background-color] duration-150',
            'hover:border-muted-foreground/35 hover:bg-muted/30 hover:shadow-md',
            'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background'
          )}
        >
          {/* Hit target for open-chart; stays under interactive actions (z-order + pointer-events) */}
          <Link
            to={toFocus}
            className={cn(
              'absolute inset-0 z-[1] rounded-lg',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background'
            )}
            aria-label={`Open chart: ${chart.title}`}
          >
            <span className="sr-only">Open chart</span>
          </Link>

          <div className="relative z-[2] flex flex-col gap-4 p-6 pointer-events-none">
            <div className="relative flex gap-4">
              <div className="min-w-0 flex-1 pr-28">
                <h3 className="text-base font-semibold tracking-tight text-foreground">{chart.title}</h3>
                <p className="mt-2 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{chart.description}</p>
              </div>
              <div className="pointer-events-auto absolute right-0 top-0 z-[3] flex items-center gap-1">
                <Tooltip content={isPinned ? 'Unpin from Overview' : 'Pin to Overview'} position="bottom">
                  <button
                    type="button"
                    className={ACTION_ICON_BTN}
                    aria-label={isPinned ? 'Unpin chart from Overview' : 'Pin chart to Overview'}
                    title={isPinned ? 'Unpin from Overview' : 'Pin to Overview'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTogglePin(chart.id);
                    }}
                  >
                    {isPinned ? <PinOff className="h-4 w-4 shrink-0" aria-hidden /> : <Pin className="h-4 w-4 shrink-0" aria-hidden />}
                  </button>
                </Tooltip>
                <Tooltip content="Open chart to export PNG or JPG from the toolbar" position="bottom">
                  <Link
                    className={ACTION_ICON_BTN}
                    to={toFocus}
                    aria-label="Open chart for export"
                    title="Open chart for export"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download className="h-4 w-4 shrink-0" aria-hidden />
                  </Link>
                </Tooltip>
                <Tooltip content="Chart definition and methodology" position="bottom">
                  <button
                    type="button"
                    className={ACTION_ICON_BTN}
                    aria-label="Chart definition and methodology"
                    title="Chart definition"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDefinitionChart(chart);
                    }}
                  >
                    <Info className="h-4 w-4 shrink-0" aria-hidden />
                  </button>
                </Tooltip>
              </div>
            </div>

            {chart.category !== 'Schedule' && chart.category !== 'Milestones' ? (
              <span className="inline-flex w-fit items-center rounded border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground">
                {chart.category}
              </span>
            ) : null}

            <div className="h-[260px] min-h-[260px] w-full shrink-0 rounded-md bg-muted/20 ring-1 ring-border/60">
              {chartsLoading ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  <LoadingState message="" size="sm" fullScreen={false} />
                </div>
              ) : hasChartData ? (
                <div className="pointer-events-none flex h-full min-h-0 w-full flex-1">{renderChart(chart.id, previewHeight, 'preview')}</div>
              ) : (
                <div className="flex h-full items-center justify-center gap-4 text-muted-foreground">
                  <BarChart3 className="h-10 w-10 shrink-0 opacity-50" aria-hidden />
                  <span className="sr-only">Select a project to load chart previews</span>
                </div>
              )}
            </div>
          </div>
        </article>
      );
    },
    [chartsLoading, handleTogglePin, hasChartData, pinnedSet, renderChart, setDefinitionChart]
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title="Chart Library"
        subtitle="Project controls analytics—browse previews, open a chart for detail and exports."
      />

      <div className="mb-8 flex flex-col gap-4">
        <div className="w-full lg:max-w-lg">
          <label htmlFor="catalog-chart-search" className="sr-only">
            Search charts
          </label>
          <input
            id="catalog-chart-search"
            type="search"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search charts…"
            autoComplete="off"
            className="h-10 w-full rounded-md border-2 border-border bg-background px-3 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
          />
        </div>

        <div className="flex flex-col gap-4 xl:flex-row xl:flex-wrap xl:items-center xl:justify-between xl:gap-6">
          <div className="flex min-w-0 flex-wrap items-center gap-3 xl:flex-1">
            <span className="sr-only">Library sections</span>
            <div
              role="toolbar"
              aria-label="Library sections"
              className="flex max-w-full flex-wrap gap-1 rounded-lg border border-border bg-muted/15 p-1"
            >
              <button
                type="button"
                onClick={() => handleLibChange('all')}
                className={cn(chipBtn, libGroup === 'all' ? chipActiveClass : chipInactiveClass)}
              >
                All charts
              </button>
              {CATALOG_LIBRARY_FILTER_GROUPS.map(({ lib, label }) => (
                <button
                  key={lib}
                  type="button"
                  onClick={() => handleLibChange(lib)}
                  className={cn(chipBtn, libGroup === lib ? chipActiveClass : chipInactiveClass)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="hidden h-8 w-px shrink-0 bg-border sm:block" aria-hidden />

            <span className="sr-only">Registry categories</span>
            <div
              role="toolbar"
              aria-label="Data categories from schedule"
              className="flex max-w-full flex-wrap gap-1 rounded-lg border border-border bg-muted/15 p-1"
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => handleCategoryChange(cat)}
                  className={cn(chipBtn, categoryParam === cat ? chipActiveClass : chipInactiveClass)}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grow xl:grow-0" />

            <div className="flex items-center gap-3 xl:ml-0">
              <span className="text-xs font-medium text-muted-foreground whitespace-nowrap" id="pinned-only-label">
                Pinned only
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={pinnedOnly}
                aria-labelledby="pinned-only-label"
                title={pinnedOnly ? 'Showing pinned charts only' : 'Showing all charts'}
                onClick={handlePinnedToggle}
                className={cn(
                  'relative inline-flex h-8 w-11 shrink-0 cursor-pointer rounded-full border border-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background',
                  pinnedOnly ? 'bg-primary text-primary-foreground' : 'bg-muted'
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'pointer-events-none absolute top-1 left-1 inline-block h-6 w-6 rounded-full bg-background shadow-sm ring-1 ring-black/5 transition-transform dark:ring-white/10',
                    pinnedOnly ? 'translate-x-4' : 'translate-x-0'
                  )}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {visibleCharts.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
          No charts match the current filter.
        </div>
      ) : (
        <div className="flex flex-col gap-16 pb-8">
          {CATALOG_LIBRARY_SECTIONS.filter((s) => s.id !== 'trends').map((section) => {
            const charts = coreLiveCharts.filter((c) => section.chartIds.includes(c.id));
            if (charts.length === 0) return null;
            return (
              <section key={section.id} aria-labelledby={`section-${section.id}`}>
                <div className="mb-4 border-b border-border pb-2">
                  <h2 id={`section-${section.id}`} className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.title}
                  </h2>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{charts.map((c) => renderCard(c))}</div>
              </section>
            );
          })}

          <section aria-labelledby="section-trends">
            <div className="mb-4 border-b border-border pb-2">
              <h2 id="section-trends" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Snapshot Trends
              </h2>
              <p className="mt-2 max-w-xl text-[11px] leading-relaxed text-muted-foreground">
                Compare metrics across schedule imports. Requires at least two snapshots.
              </p>
            </div>

            {trendLiveCharts.length > 0 ? (
              <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {trendLiveCharts.map((c) => renderCard(c))}
              </div>
            ) : null}

            {blockedSnapshotCharts.length > 0 ? (
              <details open={false} className="group rounded-lg border border-dashed border-border bg-muted/10">
                <summary
                  className={cn(
                    'flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 text-sm outline-none transition-colors hover:bg-muted/40',
                    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset [&::-webkit-details-marker]:hidden'
                  )}
                >
                  <span>
                    <span className="font-medium text-foreground">Unavailable until more snapshots exist</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {blockedSnapshotCharts.length} chart{blockedSnapshotCharts.length === 1 ? '' : 's'} — previews hidden.
                    </span>
                  </span>
                  <ChevronDown
                    aria-hidden
                    className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180"
                  />
                </summary>
                <div className="divide-y divide-border border-t border-border">
                  {blockedSnapshotCharts.map((chart) => (
                    <div key={chart.id} className="flex flex-wrap items-center gap-4 px-4 py-3 text-sm hover:bg-muted/30">
                      <Link
                        to={focusHref(chart.id)}
                        className="min-w-0 flex-1 font-medium text-foreground underline-offset-2 hover:underline"
                      >
                        {chart.title}
                      </Link>
                      <span className="shrink-0 text-xs text-muted-foreground">{chart.category}</span>
                      <div className="ml-auto flex shrink-0 items-center gap-1">
                        <Tooltip content={pinnedSet.has(chart.id) ? 'Unpin from Overview' : 'Pin to Overview'} position="bottom">
                          <button
                            type="button"
                            className={ACTION_ICON_BTN}
                            aria-label={pinnedSet.has(chart.id) ? 'Unpin' : 'Pin'}
                            title={pinnedSet.has(chart.id) ? 'Unpin' : 'Pin'}
                            onClick={() => handleTogglePin(chart.id)}
                          >
                            {pinnedSet.has(chart.id) ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                          </button>
                        </Tooltip>
                        <Tooltip content="Chart definition and methodology" position="bottom">
                          <button
                            type="button"
                            className={ACTION_ICON_BTN}
                            aria-label="Chart definition"
                            title="Chart definition"
                            onClick={() => setDefinitionChart(chart)}
                          >
                            <Info className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            ) : null}
          </section>
        </div>
      )}

      <ChartDefinitionDrawer
        open={!!definitionChart}
        onClose={() => setDefinitionChart(null)}
        chart={definitionChart}
      />
    </div>
  );
}
